/**
 * Putting a server error next to the field that caused it.
 *
 * Before this, every admin form rendered one alert directly above its
 * submit button — so a duplicate-email 409 appeared at the bottom of the
 * page, far from the email input, and the operator had to work out which
 * field it meant. Two changes:
 *
 * 1. **Errors that belong to a field render at that field.** Each module
 *    declares a `detail` code → field-name map; anything mapped is shown
 *    inline, anything unmapped falls back to a summary.
 * 2. **The summary moves to the TOP of the form.** A message below the
 *    fold, under the button, is a message an operator scrolls past.
 *
 * The server stays the authority. This only decides *where* its answer is
 * displayed, never whether the answer is right. The inline message itself is
 * the shared `FieldError` from `@speaking-coach/ui`.
 *
 * This duck-types on `{ detail }` rather than importing an error class.
 * That was originally to avoid pulling every form into one module's import
 * graph, back when there were seven error classes; since ADR-0056 there is
 * only `AdminApiError`, and the duck-typing is what let this file survive
 * that collapse untouched. Worth keeping for the same reason — it depends
 * on the shape the server guarantees, not on our transport.
 */

import { Notice } from '@speaking-coach/ui'

/** A server failure, resolved to where it should be displayed. */
export interface ResolvedFailure<F extends string = string> {
  /** The field it belongs to, or null for a form-level message. */
  field: F | null
  /** Operator-readable text, already mapped by the module's messageFor…. */
  message: string
}

interface HasDetail {
  detail?: string
}

/**
 * Split a thrown client error into "belongs to this field" or
 * "belongs to the form".
 *
 * `fieldFor` maps the server's stable `detail` codes to field names. A code
 * that is absent from the map — or a 422 whose `detail` is a Pydantic array
 * with no code at all, or a network failure — becomes a form-level message.
 * That fallback is deliberate: an unmapped error must still be *shown*,
 * because silently dropping it is worse than showing it in the wrong place.
 */
export function resolveFailure<F extends string>(
  err: unknown,
  fieldFor: Readonly<Record<string, F>>,
  message: (e: unknown) => string,
): ResolvedFailure<F> {
  const detail = (err as HasDetail | null)?.detail
  return {
    field: detail !== undefined && detail in fieldFor ? fieldFor[detail]! : null,
    message: message(err),
  }
}

/**
 * The form-level summary, at the TOP of the form.
 *
 * Only for failures that map to no field. When a failure DOES map to a
 * field, this stays silent and the field speaks — one message, in the place
 * the operator is already looking.
 */
export function FormError({ message }: { message: string | null }): JSX.Element | null {
  if (message === null) return null
  return <Notice variant="danger">{message}</Notice>
}

/** Convenience: the message for `field`, or null when the failure is elsewhere. */
export function messageFor<F extends string>(
  failure: ResolvedFailure<F> | null,
  field: F,
): string | null {
  return failure !== null && failure.field === field ? failure.message : null
}

/** Convenience: the message when it belongs to no field, else null. */
export function formMessage<F extends string>(
  failure: ResolvedFailure<F> | null,
): string | null {
  return failure !== null && failure.field === null ? failure.message : null
}
